// use history from react-router-dom
// eslint-disable-next-line import/no-extraneous-dependencies
import { Box } from '@wix/design-system';
import { IframeQueryParameters } from '@wix/wix-chatbot-common/browser';
import { Action, createPath } from 'history';
import React, { FC, useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useNavigationType,
  useSearchParams,
} from 'react-router-dom';

import { updateHelpWidgetSession } from '../common/api/updateHelpWidgetSession';
import { selectIsLiveChat } from '../common/selectors/chat';
import { selectWidgetSessionState } from '../common/selectors/widgetSession';
import { homepageService } from '../common/services/homepage/homepageService';
import { postUrlChange } from '../messageChannel/postNewMessages';
import { Article } from '../pages/Article';
import { Categories } from '../pages/Categories';
import { Category } from '../pages/Category';
import { ChatbotPage } from '../pages/Chatbot';
import { Home } from '../pages/Home';
import { SolutionList } from '../pages/SolutionList';
import { emitter } from '../util/emitter';
import {
  AppRoutePatterns,
  appRoutes,
  dontFireBiEventNavigateState,
  isChatbotArticleState,
} from './appRoutes';

export const RouterProvider: FC = () => {
  const widgetSessionState = useSelector(selectWidgetSessionState);
  const isLiveChat = useSelector(selectIsLiveChat);
  const [searchParams] = useSearchParams();
  const isOpen =
    searchParams.get(IframeQueryParameters.isChatbotOpen) !== 'false';
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const skipNextLocationChange = useRef<boolean>(false);

  const updateSession = useCallback(
    async ({ removeLast, replacedUrl, addedUrl }) => {
      await updateHelpWidgetSession({
        id: widgetSessionState.sessionTabId,
        removeLast,
        replacedUrl,
        addedUrl,
      });
    },
    [widgetSessionState.sessionTabId],
  );

  useEffect(() => {
    if (isLiveChat) {
      // we should always load chatbot screen until live conversation has ended
      navigate(appRoutes.chatbot());
    }
  }, []);

  // Let the widget know the URL changed for notifications sync purposes
  useEffect(() => {
    postUrlChange(createPath(location));
  }, [location.pathname]);

  useEffect(() => {
    emitter.on('updateArticleUri', (articleUri) => {
      if (articleUri) {
        const pathname = appRoutes.articleByUri({
          articleUri,
        });
        if (pathname !== location.pathname) {
          navigate(
            {
              pathname,
              search: searchParams.toString(),
            },
            {
              state: {
                ...dontFireBiEventNavigateState,
                ...isChatbotArticleState,
              },
            },
          );
        }
      }
    });
    emitter.on('updateCategoryUri', (categoryUri) => {
      if (categoryUri) {
        const pathname = appRoutes.categoryByUri({ categoryUri });
        if (pathname !== location.pathname) {
          navigate({
            pathname,
            search: searchParams.toString(),
          });
        }
      }
    });
    if (homepageService.homepageEnabled) {
      // TODO:homepage rename 'toggleChatbotVisible' to 'openChatbotPage' after merge 'specs.engage.enableHomepage'
      emitter.on('toggleChatbotVisible', (visible) => {
        if (visible) {
          navigate(appRoutes.chatbot(), {
            state: dontFireBiEventNavigateState,
          });
        }
      });
    }

    return () => {
      emitter.remove('updateArticleUri');
      emitter.remove('updateCategoryUri');
      homepageService.homepageEnabled && emitter.remove('toggleChatbotVisible');
    };
  }, [location.pathname]);

  useEffect(() => {
    // URL changed event(user navigates to other page in another browser tab)
    const { addedUrl, replacedUrl, removeLast } = widgetSessionState;

    if (addedUrl) {
      navigate(addedUrl);
    } else if (replacedUrl) {
      navigate(replacedUrl, { replace: true });
    } else if (removeLast) {
      navigate(-1);
    }

    skipNextLocationChange.current = true;
  }, [widgetSessionState]);

  useEffect(() => {
    if (skipNextLocationChange.current) {
      skipNextLocationChange.current = false;
      return;
    }

    const url = createPath(location);

    switch (navigationType) {
      case Action.Pop:
        void updateSession({ removeLast: true });
        break;
      case Action.Push:
        void updateSession({ addedUrl: url });
        break;
      case Action.Replace:
        void updateSession({ replacedUrl: url });
        break;
      default:
        throw new Error(`Unexpected navigationType: ${navigationType}`);
    }
  }, [location]);

  return (
    <Box
      dataHook="help-center-main"
      direction="vertical"
      height={homepageService.homepageEnabled ? '100%' : 'calc(100% - 108px)'}
      width="100%"
      maxWidth="986px"
      // Hide help center if chatbot is open
      // Used for A11y mostly
      visibility={
        isOpen && !homepageService.homepageEnabled ? 'hidden' : 'visible'
      }
    >
      <Routes>
        <Route path={AppRoutePatterns.Home} element={<Home />} />
        <Route path={AppRoutePatterns.Chatbot} element={<ChatbotPage />} />
        <Route path={AppRoutePatterns.Categories} element={<Categories />} />
        <Route path={AppRoutePatterns.CategoryByUri} element={<Category />} />
        <Route path={AppRoutePatterns.CategoryById} element={<Category />} />
        <Route path={AppRoutePatterns.Search} element={<SolutionList />} />
        <Route path={AppRoutePatterns.ArticleById} element={<Article />} />
        <Route path={AppRoutePatterns.ArticleByUri} element={<Article />} />
        {/* TODO:router use Not Found(404) page here*/}
        <Route
          path="*"
          element={<Navigate to={AppRoutePatterns.Categories} replace />}
        />
      </Routes>
    </Box>
  );
};
